import axios from 'axios';

// Detect if accessing from mobile device and use appropriate API URL
const getApiUrl = () => {
  console.log('🔍 Detecting API URL...');
  console.log('🔍 Current hostname:', window.location.hostname);
  console.log('🔍 Current protocol:', window.location.protocol);
  console.log('🔍 Environment API URL:', process.env.REACT_APP_API_URL);
  
  // If env variable is set, use it
  if (process.env.REACT_APP_API_URL) {
    console.log('✅ Using env variable:', process.env.REACT_APP_API_URL);
    return process.env.REACT_APP_API_URL;
  }
  
  // If accessing from localhost, use localhost
  if (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1') {
    console.log('✅ Using localhost for development');
    return 'http://localhost:8000';
  }
  
  // Production: Use same protocol and host as frontend
  const isHttps = window.location.protocol === 'https:';
  const productionUrl = `${isHttps ? 'https' : 'http'}://${window.location.hostname}`;
  console.log('✅ Using production URL:', productionUrl);
  return productionUrl;
};

const API_BASE_URL = getApiUrl() + '/api';
console.log('🔗 API URL configured as:', API_BASE_URL);

class AuthService {
  constructor() {
    this.token = localStorage.getItem('userToken');
    this.setupAxiosInterceptors();
  }

  setupAxiosInterceptors() {
    axios.interceptors.request.use(
      (config) => {
        const token = this.token || localStorage.getItem('userToken');
        if (token) {
          config.headers.Authorization = `Bearer ${token}`;
        }
        return config;
      },
      (error) => Promise.reject(error)
    );

    axios.interceptors.response.use(
      (response) => response,
      (error) => {
        if (error.response?.status === 401) {
          console.log('🔐 401 error intercepted:', {
            url: error.config?.url,
            hasToken: !!this.token,
            currentPath: window.location.pathname
          });
          
          // Only logout if we have a token (meaning user was authenticated)
          // and this isn't a login attempt
          const isLoginAttempt = error.config?.url?.includes('/auth/login');
          if (this.token && !isLoginAttempt) {
            console.log('🚪 Logging out due to 401 error');
            this.logout();
            // Only redirect if not already on login page
            if (window.location.pathname !== '/login') {
              console.log('🔄 Redirecting to login page');
              window.location.href = '/login';
            }
          } else {
            console.log('🔐 Not logging out:', { 
              reason: !this.token ? 'no token' : 'login attempt'
            });
          }
        }
        return Promise.reject(error);
      }
    );
  }

  async register(userData) {
    try {
      console.log('AuthService register - sending request to:', `${API_BASE_URL}/auth/register`);
      console.log('AuthService register - payload:', JSON.stringify(userData, null, 2));
      
      const response = await axios.post(`${API_BASE_URL}/auth/register`, userData);
      
      console.log('AuthService register - response status:', response.status);
      console.log('AuthService register - response data:', JSON.stringify(response.data, null, 2));
      
      return response.data;
    } catch (error) {
      console.error('AuthService register - error details:');
      console.error('- Error message:', error.message);
      console.error('- Status:', error.response?.status);
      console.error('- Status text:', error.response?.statusText);
      console.error('- Error data:', JSON.stringify(error.response?.data, null, 2));
      console.error('- Full error object:', error);
      
      // Detect specific error types for better UX
      const errorMessage = error.response?.data?.message || error.response?.data?.error || error.message || 'Registration failed';
      const errorType = this.detectRegistrationErrorType(errorMessage, error.response?.status);
      
      // Throw structured error with type and user-friendly message
      const errorToThrow = {
        message: errorMessage,
        type: errorType,
        userMessage: this.getRegistrationUserMessage(errorType, errorMessage),
        status: error.response?.status,
        response: error.response
      };
      
      console.log('AuthService register - structured error:', errorToThrow);
      throw errorToThrow;
    }
  }

  detectRegistrationErrorType(message, status) {
    const messageLower = message.toLowerCase();
    
    // Check for account already exists patterns
    if (messageLower.includes('already exists') || 
        messageLower.includes('already registered') || 
        messageLower.includes('already taken') ||
        messageLower.includes('email already') ||
        messageLower.includes('username already') ||
        status === 409) {
      return 'ACCOUNT_EXISTS';
    }
    
    // Check for validation errors
    if (messageLower.includes('invalid email') ||
        messageLower.includes('invalid username') ||
        messageLower.includes('password too short') ||
        status === 400) {
      return 'VALIDATION_ERROR';
    }
    
    // Default to generic error
    return 'GENERIC_ERROR';
  }

  getRegistrationUserMessage(errorType, originalMessage) {
    switch (errorType) {
      case 'ACCOUNT_EXISTS':
        return 'An account with this email or username already exists. Please sign in instead or use different credentials.';
      case 'VALIDATION_ERROR':
        return originalMessage; // Use the specific validation message from server
      default:
        return 'Registration failed. Please try again or contact support if the problem persists.';
    }
  }

  async login(credentials) {
    try {
      const response = await axios.post(`${API_BASE_URL}/auth/login`, credentials);
      
      // Handle both response structures (response.data.data or response.data directly)
      const responseData = response.data.data || response.data;
      const { token, user } = responseData;
      
      if (token) {
        this.token = token;
        localStorage.setItem('userToken', token);
        localStorage.setItem('userId', user._id);
        localStorage.setItem('userData', JSON.stringify(user));
        
        // Update axios default header for authenticated requests
        axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
      }
      
      return responseData;
    } catch (error) {
      console.error('AuthService login - error details:');
      console.error('- Error message:', error.message);
      console.error('- Status:', error.response?.status);
      console.error('- Error data:', JSON.stringify(error.response?.data, null, 2));
      
      // Detect specific error types for better UX
      const errorMessage = error.response?.data?.message || error.response?.data?.error || error.message || 'Login failed';
      const errorType = this.detectLoginErrorType(errorMessage, error.response?.status);
      
      // Throw structured error with type and user-friendly message
      const errorToThrow = {
        message: errorMessage,
        type: errorType,
        userMessage: this.getLoginUserMessage(errorType, errorMessage),
        status: error.response?.status,
        response: error.response
      };
      
      console.log('AuthService login - structured error:', errorToThrow);
      throw errorToThrow;
    }
  }

  detectLoginErrorType(message, status) {
    const messageLower = message.toLowerCase();
    
    // Check for account not found patterns
    if (messageLower.includes('user not found') || 
        messageLower.includes('account not found') || 
        messageLower.includes('no user found') ||
        messageLower.includes('does not exist') ||
        status === 404) {
      return 'ACCOUNT_NOT_FOUND';
    }
    
    // Check for invalid credentials patterns
    if (messageLower.includes('invalid credentials') ||
        messageLower.includes('wrong password') ||
        messageLower.includes('incorrect password') ||
        messageLower.includes('authentication failed') ||
        status === 401) {
      return 'INVALID_CREDENTIALS';
    }
    
    // Default to generic error
    return 'GENERIC_ERROR';
  }

  getLoginUserMessage(errorType, originalMessage) {
    switch (errorType) {
      case 'ACCOUNT_NOT_FOUND':
        return 'No account found with these credentials. Please check your email/username or create a new account.';
      case 'INVALID_CREDENTIALS':
        return 'Invalid email/username or password. Please check your credentials and try again.';
      default:
        return 'Login failed. Please check your credentials and try again.';
    }
  }

  logout() {
    console.log('🚪 AuthService logout called');
    this.token = null;
    localStorage.removeItem('userToken');
    localStorage.removeItem('userId');
    localStorage.removeItem('userData');
    
    // Clear axios header
    delete axios.defaults.headers.common['Authorization'];
    
    console.log('🚪 Logout complete - localStorage cleared');
  }

  isAuthenticated() {
    const token = this.token || localStorage.getItem('userToken');
    const userData = localStorage.getItem('userData');
    const userId = localStorage.getItem('userId');
    
    console.log('🔐 Checking authentication:', { 
      hasToken: !!token, 
      hasUserData: !!userData,
      hasUserId: !!userId,
      tokenLength: token?.length || 0
    });
    
    // All three pieces must be present for valid authentication
    if (!token || !userData || !userId) {
      console.log('🔐 Authentication incomplete - missing required data');
      // Clear incomplete auth state
      this.logout();
      return false;
    }
    
    // Validate userData can be parsed
    try {
      const parsedUserData = JSON.parse(userData);
      if (!parsedUserData._id || parsedUserData._id !== userId) {
        console.log('🔐 User data mismatch - clearing auth state');
        this.logout();
        return false;
      }
    } catch (error) {
      console.log('🔐 Invalid user data format - clearing auth state');
      this.logout();
      return false;
    }
    
    return true;
  }

  getUser() {
    try {
      const userData = localStorage.getItem('userData');
      if (!userData) {
        console.log('🔐 No user data in localStorage');
        return null;
      }
      
      const parsed = JSON.parse(userData);
      console.log('🔐 Parsed user data:', parsed);
      return parsed;
    } catch (error) {
      console.error('🔐 Error parsing user data:', error);
      return null;
    }
  }
}

export default new AuthService();